import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { basePath, appVersion } from '@atmi/core/injection';
import { debounceTime, Subject } from 'rxjs';

interface ErrorReport {
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  appVersion: string;
  timestamp: number;
  context?: string;
}

@Injectable()
export class TelemetryService {
  private errorBuffer: ErrorReport[] = [];
  private errorBatchSubject = new Subject<ErrorReport[]>();
  private lastErrorHash = new Map<string, number>();
  private readonly DEDUP_WINDOW_MS = 30000;
  private readonly BATCH_DELAY_MS = 2000;
  private readonly MAX_BUFFER_SIZE = 10;
  private readonly TELEMETRY_ENDPOINT = '/telemetry/errors';

  constructor(
    private readonly http: HttpClient,
    @Inject(basePath) private readonly baseUrl: string,
    @Inject(appVersion) private readonly appVersionValue: string,
  ) {
    this.initializeBatchSender();
  }

  private initializeBatchSender(): void {
    this.errorBatchSubject
      .pipe(debounceTime(this.BATCH_DELAY_MS))
      .subscribe((errors) => {
        if (errors.length > 0) {
          this.sendErrorBatch(errors);
        }
      });
  }

  reportError(error: Error | any, context?: string): void {
    try {
      const errorReport = this.buildErrorReport(error, context);

      if (this.isDuplicate(errorReport)) {
        return;
      }

      this.errorBuffer.push(errorReport);

      if (this.errorBuffer.length >= this.MAX_BUFFER_SIZE) {
        this.flushBuffer();
      } else {
        this.errorBatchSubject.next([...this.errorBuffer]);
      }
    } catch (e) {
      console.error('Failed to report error to telemetry:', e);
    }
  }

  private buildErrorReport(error: Error | any, context?: string): ErrorReport {
    const message = error?.message || String(error) || 'Unknown error';
    const stack = error?.stack || undefined;

    return {
      message,
      stack,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      appVersion: this.appVersionValue,
      timestamp: Date.now(),
      context,
    };
  }

  private isDuplicate(errorReport: ErrorReport): boolean {
    const hash = `${errorReport.message}|${errorReport.stack || ''}`;
    const lastTime = this.lastErrorHash.get(hash);
    const now = Date.now();

    if (lastTime && now - lastTime < this.DEDUP_WINDOW_MS) {
      return true;
    }

    this.lastErrorHash.set(hash, now);
    return false;
  }

  private flushBuffer(): void {
    if (this.errorBuffer.length > 0) {
      const batch = [...this.errorBuffer];
      this.errorBuffer = [];
      this.sendErrorBatch(batch);
    }
  }

  private sendErrorBatch(errors: ErrorReport[]): void {
    const telemetryUrl = `${this.baseUrl}${this.TELEMETRY_ENDPOINT}`;

    errors.forEach((error) => {
      this.http.post(telemetryUrl, error).subscribe({
        error: (err) => {
          console.debug('Telemetry request failed (non-critical):', err);
        },
      });
    });
  }
}
